/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Img, Box, useTheme} from '@chakra-ui/react'
import {getResponsiveImageAttributes} from '../../utils/responsive-image'
import {Helmet} from 'react-helmet'
import {useNonce} from '../../hooks'
import {useGesture} from '@use-gesture/react'
import useImgNaturalSize from '../../hooks/use-img-natural-size'
import useZoomGestures from '../../hooks/use-zoom-gestures'
import useResizeObserver from '../../hooks/use-resize-observer'

const IS_TEST = process.env.NODE_ENV === 'test'

// DO NOT USE MODERN JS INSIDE OR REFERENCE ANYTHING OUTSIDE OF THIS FUNCTION
const preloadImageFunction = (props) => {
    const image = new Image()
    image.src = props.src
    image.srcset = props.srcSet
    image.sizes = props.sizes
    image.loading = 'eager'
}

/**
 * Quickly create a responsive image using your dynamic image service
 *
 * ZOOM FUNCTIONALITY:
 * When enableZoom is true, the component uses multiple mechanisms to ensure zoom works reliably:
 * 1. getBoundingClientRect() - Called initially when the component mounts to get container dimensions
 * 2. ResizeObserver - Monitors container size changes after initial render
 * 3. Default maxScale of 2 - Used when container dimensions are zero (hidden elements)
 *
 * @example
 *  // Widths without a unit are interpreted as px values
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={[100, 360, 720]} />
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={{base: 100, sm: 360, md: 720}} />
 * // You can also use units of px or vw
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={['50vw', '100vw', '500px']} />
 * // With zoom enabled
 * <DynamicImage src="http://example.com/image.jpg[?sw={width}&q=60]" widths={[400, 800]} enableZoom />
 */
const DynamicImage = ({src, widths, imageProps, as, enableZoom, preload, ...rest}) => {
    const Component = as ? as : Img
    const theme = useTheme()
    const nonce = useNonce()

    // The state is true if the image should be preloaded, but once client is hydrated, we set it to false as no more preloading is needed
    const [shouldPreload, setShouldPreload] = useState(preload)

    useEffect(() => {
        setShouldPreload(false)
    }, [])

    const responsiveImageProps = useMemo(
        () => getResponsiveImageAttributes({src, widths, breakpoints: theme.breakpoints}),
        [src, widths, theme.breakpoints]
    )

    const imageContainerRef = useRef(null)
    const gestureRef = useRef(null)

    const [imageContainerSize, _setImageContainerSize] = useState(null)
    const setImageContainerSize = useCallback((size) => {
        _setImageContainerSize((current) => {
            if (!current || current.width !== size.width || current.height !== size.height) {
                return {width: size.width, height: size.height}
            }
            return current
        })
    }, [])
    const setImageContainer = useCallback(
        (el) => {
            imageContainerRef.current = el
            if (el) {
                setImageContainerSize(el.getBoundingClientRect())
            }
        },
        [setImageContainerSize]
    )
    // Monitor container size changes
    useResizeObserver(imageContainerRef.current, (entries) => {
        if (entries.length > 0) {
            setImageContainerSize(entries[0].contentRect)
        }
    })
    const naturalSize = useImgNaturalSize(gestureRef.current)
    const maxScale = useMemo(() => {
        if (!enableZoom) {
            return 1
        }
        if (!imageContainerSize || !naturalSize) {
            return 2
        }
        // Guard against cases where the image container is hidden, collapsed, or not yet rendered
        if (imageContainerSize.width <= 0 || imageContainerSize.height <= 0) {
            return 2
        }
        return Math.max(
            1,
            Math.max(
                naturalSize.width / imageContainerSize.width,
                naturalSize.height / imageContainerSize.height
            )
        )
    }, [enableZoom, naturalSize, imageContainerSize])

    const {state, gestureHandlers, gestureConfig} = useZoomGestures({
        enabled: enableZoom,
        maxScale,
        zoomableElementRef: gestureRef,
        containerRef: imageContainerRef
    })

    useGesture(gestureHandlers, gestureConfig)

    return (
        <Box
            data-testid="dynamic-image-container"
            {...rest}
            overflow="hidden"
            height="100%"
            {...(enableZoom && {
                ref: setImageContainer,
                // prevent image gallery scroll triggering when we are zoomed as the gesture is handled by useGesture
                onTouchStart: (e) => {
                    if (state.isZoomed) {
                        e.stopPropagation()
                    }
                },
                onTouchEnd: (e) => {
                    if (state.isZoomed) {
                        e.stopPropagation()
                    }
                }
            })}
        >
            {
                // DO NOT INCLUDE THIS IN TESTS as it crashes istanbul coverage
                // with "ReferenceError: cov_233hcnig72 is not defined" because we
                // dynamically create a new function in the script tag
                !IS_TEST && shouldPreload && (
                    <Helmet>
                        {/* inline script to preload the image as soon as header is received by the browser */}
                        <script lang="javascript" type="text/javascript" nonce={nonce}>
                            {`(${
                                // inline the function
                                preloadImageFunction.toString()
                            })(${
                                // pass the args as json
                                JSON.stringify(responsiveImageProps)
                            })`}
                        </script>
                    </Helmet>
                )
            }
            <Component
                {...responsiveImageProps}
                {...(enableZoom && {style: {transform: state.transform, touchAction: 'none'}})}
                {...imageProps}
                {...(enableZoom && {ref: gestureRef})}
                cursor={
                    state.isZoomed
                        ? 'grab'
                        : enableZoom
                        ? 'zoom-in'
                        : imageProps?.cursor || 'default'
                }
                data-testid="dynamic-image"
                height="100%"
                width="100%"
            />
        </Box>
    )
}

DynamicImage.propTypes = {
    /**
     * Dynamic src having an optional param that can vary with widths. For example: `image[_{width}].jpg` or `image.jpg[?sw={width}&q=60]`
     */
    src: PropTypes.string,
    /**
     * Image widths relative to the breakpoints, whose units can either be px or vw or unit-less. They will be mapped to the corresponding `sizes` and `srcSet`.
     */
    widths: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    /**
     * Props to pass to the inner image component
     */
    imageProps: PropTypes.object,
    /**
     * Override with your chosen image component
     */
    as: PropTypes.elementType,
    /**
     * Enables the zoom functionality
     */
    enableZoom: PropTypes.bool,
    /**
     * Should the image be preloaded
     */
    preload: PropTypes.bool
}

export default DynamicImage
